<template>
  <div class="px-4 md:px-8 lg:px-16 py-6 max-w-7xl mx-auto">
    <!-- Page Header -->
    <div class="mb-6">
      <h1 class="text-2xl md:text-3xl font-bold text-gray-800 flex items-center">
        <svg class="w-8 h-8 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        PROFIL PENGGUNA
      </h1>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="text-xl text-gray-500">Memuatkan data...</div>
    </div>

    <!-- Profile Content -->
    <div v-else class="space-y-6">
      <!-- User Profile Card -->
      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <!-- Header Section with Avatar and Name -->
        <div class="bg-gradient-to-r from-green-500 to-green-600 p-6">
          <div class="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <!-- Avatar -->
            <div class="flex-shrink-0">
              <img
                v-if="user.image_url"
                class="h-24 w-24 sm:h-32 sm:w-32 rounded-full ring-4 ring-white shadow-lg object-cover"
                :src="user.image_url"
                :alt="user.full_name"
              />
              <img
                v-else
                class="h-24 w-24 sm:h-32 sm:w-32 rounded-full ring-4 ring-white shadow-lg object-cover"
                src="@/assets/images/user.png"
                alt="Default avatar"
              />
            </div>

            <!-- Name and Title -->
            <div class="flex-1 text-center sm:text-left">
              <h2 class="text-2xl sm:text-3xl font-bold text-white mb-2">
                {{ user.full_name }}
              </h2>
              <div class="flex flex-wrap justify-center sm:justify-start gap-4 text-white">
                <div class="flex items-center">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                  </svg>
                  <span class="text-sm font-medium">No. Ahli: {{ share.member_no }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Share Information Section -->
        <div class="p-6 bg-gray-50 border-b border-gray-200">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="bg-white rounded-lg p-4 shadow-sm">
              <div class="flex items-center">
                <div class="flex-shrink-0 bg-green-100 rounded-full p-3">
                  <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="ml-4">
                  <p class="text-sm font-medium text-gray-500">Jumlah Syer</p>
                  <p class="text-2xl font-bold text-gray-900">RM{{ share.total_share }}</p>
                  <p class="text-xs text-gray-400 mt-1">
                    {{ share.updated_at ? dateTime(share.updated_at.toDate()) : dateTime(new Date()) }}
                  </p>
                </div>
              </div>
            </div>

            <div class="bg-white rounded-lg p-4 shadow-sm">
              <div class="flex items-center">
                <div class="flex-shrink-0 bg-blue-100 rounded-full p-3">
                  <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div class="ml-4">
                  <p class="text-sm font-medium text-gray-500">No. Akaun Bank</p>
                  <p class="text-xl font-semibold text-gray-900">{{ user.bank_account || '-' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Personal Details Section -->
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center border-b pb-3">
            <svg class="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Maklumat Peribadi
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- IC Number -->
            <div>
              <label class="text-sm font-medium text-gray-500 block mb-1">No. Kad Pengenalan</label>
              <p class="text-base font-semibold text-gray-900">{{ user.ic_no || '-' }}</p>
            </div>

            <!-- Address -->
            <div class="sm:col-span-2">
              <label class="text-sm font-medium text-gray-500 block mb-1">Alamat</label>
              <p class="text-base font-semibold text-gray-900">{{ user.address || '-' }}</p>
            </div>

            <!-- Postcode -->
            <div>
              <label class="text-sm font-medium text-gray-500 block mb-1">Poskod</label>
              <p class="text-base font-semibold text-gray-900">{{ user.postcode || '-' }}</p>
            </div>

            <!-- City -->
            <div>
              <label class="text-sm font-medium text-gray-500 block mb-1">Bandar</label>
              <p class="text-base font-semibold text-gray-900">{{ user.city || '-' }}</p>
            </div>

            <!-- State -->
            <div>
              <label class="text-sm font-medium text-gray-500 block mb-1">Negeri</label>
              <p class="text-base font-semibold text-gray-900">{{ user.state || '-' }}</p>
            </div>

            <!-- Phone -->
            <div>
              <label class="text-sm font-medium text-gray-500 block mb-1">No. Telefon</label>
              <p class="text-base font-semibold text-gray-900">{{ user.phone_number || '-' }}</p>
            </div>

            <!-- Email -->
            <div>
              <label class="text-sm font-medium text-gray-500 block mb-1">Email</label>
              <p class="text-base font-semibold text-gray-900">{{ user.email || '-' }}</p>
            </div>

            <!-- Gender -->
            <div>
              <label class="text-sm font-medium text-gray-500 block mb-1">Jantina</label>
              <p class="text-base font-semibold text-gray-900">{{ user.gender || '-' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase/db";
import moment from "moment";
// import { getDownloadURL, getStorage, ref } from "@firebase/storage";

export default {
  data() {
    return {
      user: {},
      share: {
        total_share: 0,
        member_no: '',
        updated_at: null
      },
      loading: true,
    };
  },
  async created() {
    this.checkUserState();
  },
  methods: {
    checkUserState() {
      const auth = getAuth();
      // const storage = getStorage();

      onAuthStateChanged(auth, async (user) => {
        if (user) {
          // const storageRef = ref(storage, user.uid + ".jpeg");

          await onSnapshot(doc(db, "users", user.uid), async (userDocSnap) => {
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              console.log(userData);
              this.user = userData || {};
              
              if (this.user && this.user.share_id) {
                const shareInformationDocSnap = await getDoc(
                  doc(db, "shares", this.user.share_id)
                );
                if (shareInformationDocSnap.exists()) {
                  this.share = shareInformationDocSnap.data();
                  console.log(this.share);
                }
              }
              this.loading = false;
            } else {
              console.log("User document does not exist");
              this.loading = false;
            }
          });
          // getDownloadURL(storageRef).then((url) => {
          //   console.log(url);
          //   this.user.image_url = url;
          // });
        } else {
          console.log("Not authenticated");
          this.$router.push("/");
        }
      });
    },
    dateTime(value) {
      return moment(value).format("DD, MMM YYYY");
    },
  },
};
</script>

<style scoped>
/* Ensure consistent styling with ProfileForm */
</style>
